// src/pages/-[lang]/admin/reporting/dashboard-recalculation.tsx
import React, { useContext, useEffect, useState } from 'react';
import AdminLayout from '@app/layout/AdminLayout.tsx';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { ApiContext } from '@api/api';
import { DashboardBuildMetaDTO } from '@api/ringil-api.v2.tsx';
import { ToastContext } from '@components/auth/ToastContext.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import { RingilHeading } from '@components/styles.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';

interface ComponentProps {}

const POLL_INTERVAL_MS = 1000;

// The build runs asynchronously on the backend; treat it as running while the status reads as
// in-progress, or while a build was started but has no finish timestamp yet.
const isRunning = (m?: DashboardBuildMetaDTO) => !!m && (/(run|progress|pending|queue|build)/i.test(m.status ?? '') || (!!m.startedAt && !m.finishedAt));

const DashboardRecalculationPage: React.FC<ComponentProps> = () => {
  const { tr } = useTranslationLgs();
  const { adminRebuildDashboard, getAdminDashboardBuildMeta } = useContext(ApiContext);
  const { showToastMessage } = useContext(ToastContext);

  const [meta, setMeta] = useState<DashboardBuildMetaDTO>();
  const [rebuilding, setRebuilding] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const running = isRunning(meta);

  const loadMeta = () => {
    getAdminDashboardBuildMeta(data => {
      setMeta(data);
      // Auto-refresh only makes sense while a build is running — stop polling once it finishes.
      if (!isRunning(data)) setAutoRefresh(false);
    });
  };

  useEffect(() => {
    loadMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll the build status on a regular interval while auto-refresh is enabled (only active while running).
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => loadMeta(), POLL_INTERVAL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh]);

  const triggerRebuild = () => {
    setRebuilding(true);
    adminRebuildDashboard(
      data => {
        setRebuilding(false);
        showToastMessage(
          tr('admin.reporting.dashboardRecalculation.title', 'Dashboard recalculation'),
          data?.message,
          data?.started ? 'success' : 'warn',
        );
        // Once a build is kicked off, start auto-refreshing the status until it completes.
        if (data?.started) setAutoRefresh(true);
        loadMeta();
      },
      undefined,
      {
        onError: () => {
          setRebuilding(false);
          showToastMessage(
            tr('admin.reporting.dashboardRecalculation.title', 'Dashboard recalculation'),
            tr('admin.reporting.dashboardRecalculation.error', 'Failed to trigger dashboard recalculation'),
            'error',
          );
        },
      },
    );
  };

  const metaRows: { label: string; value: React.ReactNode }[] = meta
    ? [
        { label: tr('admin.reporting.meta.status', 'Status'), value: meta.status ?? '—' },
        { label: tr('admin.reporting.meta.lastBuildAt', 'Last build at'), value: meta.lastBuildAt ?? '—' },
        { label: tr('admin.reporting.meta.startedAt', 'Started at'), value: meta.startedAt ?? '—' },
        { label: tr('admin.reporting.meta.finishedAt', 'Finished at'), value: meta.finishedAt ?? '—' },
        {
          label: tr('admin.reporting.meta.duration', 'Duration'),
          value: meta.durationMs != null ? `${(meta.durationMs / 1000).toFixed(1)} s` : '—',
        },
        { label: tr('admin.reporting.meta.shipmentRows', 'Shipment rows'), value: meta.shipmentRows ?? '—' },
        { label: tr('admin.reporting.meta.savingsRows', 'Savings rows'), value: meta.savingsRows ?? '—' },
        { label: tr('admin.reporting.meta.reservationRows', 'Reservation rows'), value: meta.reservationRows ?? '—' },
        { label: tr('admin.reporting.meta.supplierNotificationRows', 'Supplier notification rows'), value: meta.supplierNotificationRows ?? '—' },
        { label: tr('admin.reporting.meta.rampDayRows', 'Ramp-day rows'), value: meta.rampDayRows ?? '—' },
      ]
    : [];

  return (
    <AdminLayout title={tr('admin.reporting.dashboardRecalculation.title', 'Dashboard recalculation')}>
      <div className="flex flex-col gap-4">
        <RingilHeading>{tr('admin.reporting.dashboardRecalculation.title', 'Dashboard recalculation')}</RingilHeading>

        <p className="text-content-secondary max-w-[640px]">
          {tr(
            'admin.reporting.dashboardRecalculation.description',
            'Triggers a full recomputation of the dashboard statistics (shipments, reservations and supplier notifications). The build runs asynchronously on the backend.',
          )}
        </p>

        <div>
          <RingilButton
            mode={'filled'}
            label={tr('admin.reporting.dashboardRecalculation.trigger', 'Trigger recalculation')}
            loading={rebuilding}
            onClick={triggerRebuild}
          />
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex flex-row items-center gap-3">
            <h3 className="text-lg font-medium">{tr('admin.reporting.meta.heading', 'Last build')}</h3>
            {running && <span className="text-xs font-medium text-content-brand">{tr('admin.reporting.meta.running', 'Running…')}</span>}
            <RingilButton mode={'text-only'} size={'small'} label={tr('admin.reporting.meta.refresh', 'Refresh')} onClick={loadMeta} />
            <RingilButton
              mode={'text-only'}
              size={'small'}
              disabled={!running}
              label={
                autoRefresh
                  ? tr('admin.reporting.meta.stopAutoRefresh', 'Stop auto-refresh')
                  : tr('admin.reporting.meta.startAutoRefresh', 'Start auto-refresh')
              }
              onClick={() => setAutoRefresh(prev => !prev)}
            />
          </div>
          {meta ? (
            <table className="text-sm">
              <tbody>
                {metaRows.map(row => (
                  <tr key={row.label}>
                    <td className="pr-6 py-0.5 text-content-secondary">{row.label}</td>
                    <td className="py-0.5">{row.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-content-secondary">{tr('admin.reporting.meta.none', 'No build metadata available yet.')}</div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default withAuthenticationRequired(DashboardRecalculationPage);